import { memo, useRef } from "react";

type PropsComparator<Props> = (
  previousProps: Readonly<Props>,
  nextProps: Readonly<Props>,
) => boolean;

/** Keeps a form-local component type stable across parent renders. */
export function useStableMemoComponent<Props extends object>(
  render: (props: Props) => React.ReactNode,
  arePropsEqual?: PropsComparator<Props>,
) {
  const renderRef = useRef(render);
  const comparatorRef = useRef(arePropsEqual);
  const componentRef = useRef<React.NamedExoticComponent<Props> | null>(null);

  renderRef.current = render;
  comparatorRef.current = arePropsEqual;

  if (!componentRef.current) {
    componentRef.current = memo<Props>(
      (props: Props) => <>{renderRef.current(props)}</>,
      (previousProps, nextProps) =>
        comparatorRef.current?.(previousProps, nextProps) ?? false,
    );
  }

  return componentRef.current;
}
